import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

import type { AwardCreateSchema, AwardMutationSchema } from './schemas';
import type { AwardQueryAllSchema, AwardQuerySchema } from './schemas/award-query-schema';

export function getAwardsQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['AWARDS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<AwardQueryAllSchema>({
        endpoint: '/api/v1/awards?include=brand.icon,client.logo',
        params: `${apiParamStr}`,
      });

      return res;
    },
  };
}

export function useGetAwards(apiParamStr: string) {
  return useSuspenseQuery(getAwardsQuery(apiParamStr));
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['AWARDS'] });
};

export function useGetAward(id: string) {
  return useSuspenseQuery(getAwardQuery(id));
}

export function getAwardQuery(id: string) {
  return {
    queryKey: ['AWARD', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: AwardQuerySchema }>({
        endpoint: `/api/v1/awards/${id}`,
        params: `?include=brand.icon,client.logo,gallery`,
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const invalidateAwardQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['AWARD', id] });
};

export const useGetAwardsMutation: UseControlledMutation<AwardQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['AWARDS_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<AwardQueryAllSchema>({
            endpoint: '/api/v1/awards',
            params: { search, per_page },
          });

          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export const useCreateMutation = () =>
  useMutation<{ data: AwardQuerySchema }, unknown, AwardCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/awards/',
        payload,
      }),
  });

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: AwardQuerySchema }, ApiError, AwardMutationSchema>({
    mutationFn: ({ id: bId, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/awards/${id || bId}`,
        method: 'PUT',
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'awards', id] });
    },
  });

export const usePatchMutation = () =>
  useMutation<{ data: AwardQuerySchema }, ApiError, Partial<AwardMutationSchema>>({
    mutationFn: ({ id, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/awards/${id}`,
        method: 'PATCH',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['AWARD_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/awards/${id}`,
        method: 'DELETE',
      }),
  });
